import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { devRevService } from '../config/devrev'
import ChatInitializer from '../components/ChatInitializer'

const ChatContext = createContext()

export const useChat = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return context
}

export const ChatProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [sessionToken, setSessionToken] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [userEmail, setUserEmail] = useState(null)

  // Initialize chat with user email
  const initializeChat = useCallback(async (email, displayName = null) => {
    if (!email) {
      setError('User email is required for chat initialization')
      return false
    }

    setIsLoading(true)
    setError(null)
    setUserEmail(email)

    try {
      // Generate session token
      const tokenResult = await devRevService.generateSessionToken(email, displayName)
      
      if (tokenResult.success) {
        setSessionToken(tokenResult.session_token)
        
        // Initialize PLuG widget with session token
        const initResult = devRevService.initializeWidget(tokenResult.session_token)
        
        if (initResult) {
          setIsInitialized(true)
          console.log('✅ Chat initialized successfully for user:', email)
          return true
        } else {
          setError('Failed to initialize chat widget')
          return false
        }
      } else {
        setError(tokenResult.error || 'Failed to generate session token')
        return false
      }
    } catch (err) {
      console.error('Chat initialization error:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Initialize chat without session token (fallback)
  const initializeChatFallback = useCallback(() => {
    try {
      const initResult = devRevService.initializeWidget()
      
      if (initResult) {
        setIsInitialized(true)
        console.log('✅ Chat initialized in fallback mode')
        return true
      } else {
        setError('Failed to initialize chat widget')
        return false
      }
    } catch (err) {
      console.error('Chat fallback initialization error:', err)
      setError(err.message)
      return false
    }
  }, [])

  // Open chat with specific context
  const openChat = useCallback((context = null) => {
    if (!isInitialized) {
      console.warn('Chat not initialized')
      return false
    }

    return devRevService.openChat(context)
  }, [isInitialized])

  // Open chat for specific insurance topics
  const openClaimsChat = useCallback(() => {
    return openChat('Hi, I need help with my insurance claims. Can you assist me?')
  }, [openChat])

  const openPolicyChat = useCallback(() => {
    return openChat('Hi, I have questions about my insurance policy. Can you help me?')
  }, [openChat])

  const openGeneralChat = useCallback(() => {
    return openChat('Hi, I need assistance with my insurance. How can you help me?')
  }, [openChat])

  // Reset widget positioning to default
  const resetWidgetPositioning = useCallback(() => {
    try {
      const result = devRevService.resetWidget()
      if (result) {
        console.log('✅ Widget positioning reset to default')
        return true
      }
      return false
    } catch (err) {
      console.error('Error resetting widget positioning:', err)
      return false
    }
  }, [])

  // Check if user is identified in chat
  const isUserIdentified = useCallback(() => {
    return sessionToken !== null && userEmail !== null
  }, [sessionToken, userEmail])

  // Get user info for chat
  const getUserInfo = useCallback(() => {
    return {
      email: userEmail,
      isIdentified: isUserIdentified(),
      hasSessionToken: !!sessionToken
    }
  }, [userEmail, isUserIdentified, sessionToken])

  // Reset chat state
  const resetChat = useCallback(() => {
    setSessionToken(null)
    setUserEmail(null)
    setIsInitialized(false)
    setError(null)
    setIsLoading(false)
  }, [])

  // Auto-initialize on mount if user email is available
  useEffect(() => {
    // Component mounted
  }, [])

  const value = {
    // State
    isInitialized,
    sessionToken,
    isLoading,
    error,
    userEmail,
    
    // Actions
    initializeChat,
    initializeChatFallback,
    openChat,
    openClaimsChat,
    openPolicyChat,
    openGeneralChat,
    resetWidgetPositioning,
    
    // Utilities
    isUserIdentified,
    getUserInfo,
    resetChat
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
      <ChatInitializer />
    </ChatContext.Provider>
  )
}

export default ChatContext
